import { PathLayer, IconLayer } from '@deck.gl/layers';
import type { MapGeometry, MapLineFeature, MapStationFeature } from '@vitals/shared';
import { buildCommuterStationLayer } from './stationMarkers.js';

/**
 * The static LIRR route map: branch-colored line ribbons + station dots, drawn
 * beneath the live train bullets. Geometry is fetched once from the server's
 * cached `/api/lirr-geometry` endpoint (it never changes between schedule
 * refreshes), so these layers are built once and reused — they don't animate.
 */

/**
 * Cache-bust version for the static LIRR geometry payload. The client appends
 * it to the fetch URL (`/api/lirr-geometry?v=N`) so any change busts the long
 * HTTP cache: returning clients request a never-before-cached URL and get the
 * fresh payload instead of a stale one. BUMP THIS on any change to the emitted
 * coordinates or feature shape. (v2 = branch-colored ribbons; v3 = corner
 * rounding + tapered ribbon offsets.)
 */
export const LIRR_GEOMETRY_VERSION = 9;

/** Schema-versioned LIRR geometry URL (the epoch loader may append `&e=`). */
export function lirrGeometryUrl(baseUrl = ''): string {
  return `${baseUrl}/api/lirr-geometry?v=${LIRR_GEOMETRY_VERSION}`;
}

/** Fetch the static LIRR geometry once. Returns null on failure so the rest of
 * the map renders normally without it (honest degradation, not a hard crash). */
export async function fetchLirrGeometry(baseUrl = ''): Promise<MapGeometry | null> {
  try {
    const res = await fetch(lirrGeometryUrl(baseUrl));
    if (!res.ok) return null;
    return (await res.json()) as MapGeometry;
  } catch {
    return null;
  }
}

const LINE_ALPHA = 200; // route lines sit just under full opacity so overlaps blend
const FALLBACK_LINE: [number, number, number] = [136, 136, 136]; // grey if color missing

/** Colored line ribbons, one per branch color segment. Hover surfaces the
 * branch name (handled in tooltip.ts). */
export function buildLirrLinesLayer(geometry: MapGeometry): PathLayer<MapLineFeature> {
  return new PathLayer<MapLineFeature>({
    id: 'lirrLines',
    data: geometry.lines.features,
    getPath: (f) => f.geometry.coordinates,
    getColor: (f) => {
      const [r, g, b] = f.properties.color ?? FALLBACK_LINE;
      return [r, g, b, LINE_ALPHA];
    },
    getWidth: 2,
    widthUnits: 'pixels',
    widthMinPixels: 1,
    capRounded: true,
    jointRounded: true,
    pickable: true,
  });
}

/** Small station dots; hover surfaces the station name (handled in tooltip.ts). */
export function buildLirrStationsLayer(geometry: MapGeometry): IconLayer<MapStationFeature> {
  return buildCommuterStationLayer('lirrStations', geometry.stations.features);
}
